import { Trophy } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { SectionHeading, EmptyState } from "@/components/ui/misc";
import { CombinationCard } from "@/components/shared/CombinationCard";
import { useTopCombinations } from "@/hooks/useDerived";
import { useAppStore } from "@/store/useAppStore";
import { toast } from "@/components/ui/toast";
import type { ActionType, Combination } from "@/types";

/**
 * Section 2 — Top Combinations.
 * Top-N by usage; every tile is one-click executable, restoring the full
 * dashboard state. (The Trending tab was removed by request — Top N only.)
 */
export function TopCombinations() {
  const top = useTopCombinations();
  const apply = useAppStore((s) => s.applyCombination);

  async function run(c: Combination, action: ActionType, label: string) {
    await apply(c, action);
    toast.success(label, c.signature);
  }

  return (
    <Card className="flex flex-col">
      <CardHeader>
        <SectionHeading
          icon={Trophy}
          title={`Top ${top.length || ""} Combinations`}
          subtitle="Click any to restore that view"
        />
      </CardHeader>
      <CardContent className="max-h-[42vh] space-y-1.5 overflow-y-auto scrollbar-thin">
        {top.length === 0 ? (
          <EmptyState icon={Trophy} title="No combinations yet" description="Apply parameters to start building your top list." />
        ) : (
          top.map((c, i) => (
            <CombinationCard
              key={c.id}
              combination={c}
              rank={i + 1}
              onApply={() => run(c, "Top10_Click", "Applied top combination")}
            />
          ))
        )}
      </CardContent>
    </Card>
  );
}
